import { publicIso, publicUrl } from './publicRecords';

export type WorkKind = 'music' | 'video' | 'story' | 'tool' | 'article' | 'mixed' | 'unknown';
export type WorkFormat = 'album' | 'single' | 'playlist' | 'episode' | 'assistant' | 'article' | 'short' | 'unknown';
export type LinkPurpose = 'listen' | 'watch' | 'read' | 'use' | 'visit';
export interface ExternalLink { url: string; platform: string; purpose: LinkPurpose }
export interface Work {
    id: string; title: string; description: string; thumbnail: string | null; publishedAt: string | null;
    kind: WorkKind; format: WorkFormat; platform: string; sourceType: string; links: ExternalLink[];
    playback: { embedUrl: string | null; mediaUrl: string | null };
}
const formats: Record<string, [WorkKind, WorkFormat]> = {
    album: ['music', 'album'], single: ['music', 'single'], playlist: ['music', 'playlist'],
    episode: ['story', 'episode'], assistant: ['tool', 'assistant'], 'free article': ['article', 'article'],
    article: ['article', 'article'], short: ['video', 'short'],
    music: ['music', 'unknown'], video: ['video', 'unknown'], story: ['story', 'unknown'], tool: ['tool', 'unknown'],
};
const purpose: Record<WorkKind, LinkPurpose> = { music: 'listen', video: 'watch', story: 'read', tool: 'use', article: 'read', mixed: 'visit', unknown: 'visit' };
export function adaptWork(value: any): Work | null {
    if (!value || typeof value.id !== 'string' || !value.id.trim() || (value.title != null && typeof value.title !== 'string')) throw new Error('Invalid work identity');
    if ((value.status !== undefined && !['public', 'published'].includes(value.status)) || value.is_published === 0 || value.is_published === false || value.is_discard) return null;
    const date = typeof value.publishedAt === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.publishedAt) ? `${value.publishedAt}T00:00:00Z` : value.publishedAt;
    const publishedAt = publicIso(date);
    if (date !== undefined && date !== null && date !== '' && !publishedAt) throw new Error('Invalid work date');
    if (publishedAt && Date.parse(publishedAt) > Date.now()) return null;
    const sourceType = typeof value.type === 'string' ? value.type.trim() : '';
    const classifications = sourceType.toLowerCase().split(/[+,/]/).map((part: string) => formats[part.trim()]);
    const known = classifications.length > 0 && classifications.every(Boolean);
    const legacy = sourceType.toLowerCase() in formats ? formats[sourceType.toLowerCase()] : known && classifications.length > 1 ? ['mixed', 'unknown'] : ['unknown', 'unknown'];
    const kind: WorkKind = Object.keys(purpose).includes(value.kind) ? value.kind : legacy[0];
    const format: WorkFormat = ['album', 'single', 'playlist', 'episode', 'assistant', 'article', 'short', 'unknown'].includes(value.format) ? value.format : legacy[1];
    const platform = typeof value.platform === 'string' ? value.platform.toLowerCase() : 'unknown';
    const links: ExternalLink[] = [];
    const candidates = Array.isArray(value.links) ? value.links : [value.workUrl, value.songUrl].filter(Boolean).map(url => ({ url, platform, purpose: purpose[kind] }));
    for (const candidate of candidates) {
        const url = publicUrl(candidate?.url);
        if (!url || links.some(link => link.url === url)) continue;
        links.push({ url, platform: typeof candidate.platform === 'string' ? candidate.platform.toLowerCase() : platform,
            purpose: Object.values(purpose).includes(candidate.purpose) ? candidate.purpose : purpose[kind] });
    }
    return { id: value.id, title: typeof value.title === 'string' ? value.title.trim() : '', description: typeof value.description === 'string' ? value.description : '',
        thumbnail: publicUrl(value.thumbnail ?? value.thumbnailUrl), publishedAt, kind, format, platform, sourceType, links,
        playback: { embedUrl: publicUrl(value.playback?.embedUrl ?? value.embedUrl), mediaUrl: publicUrl(value.playback?.mediaUrl ?? value.extensionContent) } };
}
export function publicWorks(value: unknown): Work[] {
    if (!Array.isArray(value)) throw new Error('Invalid work list');
    const works = value.map(adaptWork).filter((work): work is Work => work !== null);
    if (new Set(works.map(work => work.id)).size !== works.length) throw new Error('Duplicate work id');
    return works;
}
export async function fetchWorks(): Promise<Work[]> {
    const response = await fetch('/works.json');
    if (!response.ok) throw new Error(`Works HTTP ${response.status}`);
    return publicWorks(await response.json());
}
export function workTitle(work: Pick<Work, 'title'>, fallback: string): string {
    return work.title.trim() || fallback;
}
